// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API_BASE_URL from "../config/api";
import "./StoreOrders.css";

// Helper to format date
const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    });
};

const formatPrice = (price, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency || 'INR'
    }).format(price);
};

const DEFAULT_STATUS_OPTS = ["Received", "Processing", "Left the facility", "Shipped"];

export default function StoreOrders({ fromDatabase = false }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editingOrder, setEditingOrder] = useState(null);
    const [statusList, setStatusList] = useState([]);
    const [updating, setUpdating] = useState(false);
    const [receiptTemplateName, setReceiptTemplateName] = useState(null);

    useEffect(() => {
        fetchOrders();
        fetchReceiptTemplateName();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem("token");
            if (!token) {
                setError("Please log in to view orders.");
                setLoading(false);
                return;
            }

            // Fetch a large number of orders to get "all" (or at least recent 100)
            const response = await fetch(`${API_BASE_URL}/store/orders?limit=100`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success) {
                // Filter for successful payment (paid)
                const successfulOrders = data.data.filter(order => 
                    order.payment && order.payment.status === 'paid'
                );
                setOrders(successfulOrders);
            } else {
                setError(data.message || "Failed to fetch orders");
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
            setError("Error fetching orders");
        } finally {
            setLoading(false);
        }
    };

    const fetchReceiptTemplateName = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/store/EmailTemplates?templateName=ORDER_RECEIPT`);
            const rawText = await response.text();
            const trimmed = rawText ? rawText.trim() : '';
            if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return;
            let data;
            try { data = JSON.parse(trimmed); } catch (e) { return; }
            if (data.success && data.data && data.data.length > 0) {
                setReceiptTemplateName(data.data[0].templateName);
            }
        } catch (err) {
            // silent fail
        }
    };

    const handleEditClick = (order) => {
        let initialList = [];

        const createItem = (status, message = "", checked = false, custom = false) => ({
            id: Date.now() + Math.random(),
            status,
            message,
            checked,
            custom,
            updatedAt: checked ? new Date().toISOString() : null
        });

        const trackingStatus = order.trackingDetails && order.trackingDetails.status;

        if (Array.isArray(trackingStatus) && trackingStatus.length > 0) {
            // Existing detailed status
            initialList = trackingStatus.map(s => {
                // Determine if it's the old mixed format or new object
                if (typeof s === 'string') return createItem(s, "", true, true);
                return {
                    ...s,
                    id: s.id || Date.now() + Math.random()
                };
            });
        } else {
            // Initialize with defaults
            initialList = DEFAULT_STATUS_OPTS.map(label => createItem(label));

            // Legacy check: if orderStatus has some other string, maybe add it?
            // User wants "placed"/"confirmed" ignored.
            let currentStatus = typeof order.orderStatus === 'string' ? order.orderStatus : "";
            
            if (currentStatus) {
                const navStatus = currentStatus.toLowerCase();
                if (navStatus !== 'placed' && navStatus !== 'confirmed') {
                    // It's some other status (e.g. cancelled?), maybe add it?
                    // For now, just ignore detailed list creation from simple status
                    // to force usage of new system.
                    // Or add it as custom if it's significant.
                    // The user instruction "remove this confirmed status from popup" suggests ignoring.
                }
            }
        }
        
        setStatusList(initialList);
        setEditingOrder(order);
    };

    const handleStatusUpdate = async (e) => {
        e.preventDefault();
        if (!editingOrder) return;

        try {
            setUpdating(true);
            const token = sessionStorage.getItem("token");
            
            // Save trackingDetails.status
            const response = await fetch(`${API_BASE_URL}/store/orders/${editingOrder.orderId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    trackingDetails: {
                        status: statusList
                    }
                })
            });

            const data = await response.json();

            if (data.success) {
                // Update local state
                setOrders(prev => prev.map(o => 
                    o.orderId === editingOrder.orderId ? { 
                        ...o, 
                        trackingDetails: { ...o.trackingDetails, status: statusList } 
                    } : o
                ));
                setEditingOrder(null);
            } else {
                alert(data.message || "Failed to update status");
            }
        } catch (err) {
            console.error("Error updating status:", err);
            alert("Error updating status");
        } finally {
            setUpdating(false);
        }
    };

    const handleResendEmail = async (orderId) => {
        if (!window.confirm("Are you sure you want to resend the order confirmation email?")) return;

        try {
            const token = sessionStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/store/orders/${orderId}/resend-receipt`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success) {
                alert("Email resent successfully!");
                // Optionally refresh orders to update status
                fetchOrders();
            } else {
                const errMsg = data.error ? `${data.message}\n\nReason: ${data.error}` : (data.message || "Failed to resend email");
                alert(errMsg);
            }
        } catch (err) {
            console.error("Error resending email:", err);
            alert("Error resending email");
        }
    };

    const updateStatusItem = (id, field, value) => {
        setStatusList(prev => prev.map(item => 
            item.id === id ? { 
                ...item, 
                [field]: value,
                updatedAt: new Date().toISOString() 
            } : item
        ));
    };

    const addStatusItem = (index) => {
        const newItem = {
            id: Date.now() + Math.random(),
            status: "",
            message: "",
            checked: true,
            custom: true,
            updatedAt: new Date().toISOString()
        };
        const newList = [...statusList];
        newList.splice(index + 1, 0, newItem);
        setStatusList(newList);
    };

    const removeStatusItem = (id) => {
        setStatusList(prev => prev.filter(item => item.id !== id));
    };

    // Sorting Logic: Physical first, then Digital
    // Physical group: Any order containing at least one physical product
    // Digital group: Orders containing ONLY digital products
    const sortedOrders = [...orders].sort((a, b) => {
        const isAPhysical = a.products && a.products.some(p => p.productType !== 'digital');
        const isBPhysical = b.products && b.products.some(p => p.productType !== 'digital');

        if (isAPhysical && !isBPhysical) return -1;
        if (!isAPhysical && isBPhysical) return 1;
        
        // If same type, sort by date desc
        return new Date(b.createdAt) - new Date(a.createdAt);
    });

    const isYetToReceive = (order) => {
        const statusData = order.trackingDetails && order.trackingDetails.status;
        if (Array.isArray(statusData)) {
            const activeStatuses = statusData.filter(s => s.checked);
            if (activeStatuses.length > 0) return false;
        }
        const legacy = order.orderStatus;
        if (typeof legacy === 'string') {
            const lower = legacy.toLowerCase();
            return lower === 'placed' || lower === 'confirmed';
        }
        return true;
    };

    const renderStatus = (order) => {
        const statusData = order.trackingDetails && order.trackingDetails.status;

        if (Array.isArray(statusData)) {
            const activeStatuses = statusData.filter(s => s.checked);
            if (activeStatuses.length > 0) {
                const latest = activeStatuses[activeStatuses.length - 1];
                return (
                    <span className="status-timeline-item">
                        <span className="status-main">{latest.status}</span>
                        {latest.message && <span className="status-message"> - {latest.message}</span>}
                    </span>
                );
            }
        }

        // Fallback to legacy status
        const legacy = order.orderStatus;
        if (typeof legacy === 'string') {
            const lower = legacy.toLowerCase();
            if (lower === 'placed' || lower === 'confirmed') return <span className="status-text warning">yet to receive</span>;
            return <span className="status-text">{legacy}</span>;
        }

        return <span className="status-text warning">yet to receive</span>;
    };

    if (loading) return <div className="store-orders-loading flex min-h-[180px] items-center justify-center text-[0.95rem] text-[#666]">Loading orders...</div>;
    if (error) return <div className="store-orders-error rounded-[10px] border border-[#fecaca] bg-[#fff5f5] p-[12px] text-[#b91c1c]">{error}</div>;

    return (
        <div className="store-orders-section rounded-[12px] border border-slider bg-bg p-[12px] shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
            {fromDatabase && (
                <Link to="/admin/database" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '8px' }}>
                    ← Back to Database
                </Link>
            )}
            <h2 className="orders-heading mb-[12px] text-[1.4rem] font-[800]">Store Orders</h2>
            
            {sortedOrders.length === 0 ? (
                <div className="no-orders">No successful orders found.</div>
            ) : (
                <div className="orders-grid grid grid-cols-1 gap-[12px] xl:grid-cols-2">
                    {sortedOrders.map(order => {
                        const isPhysical = order.products && order.products.some(p => p.productType !== 'digital');
                        const emailStatus = order.emailStatus === 'SENT' ? 'sent' : order.emailStatus === 'FAILED' ? 'failed' : 'pending';
                        const subtotal = order.products ? order.products.reduce((acc, p) => acc + (p.subtotal || 0), 0) : 0;
                        const discount = subtotal - (order.totalAmount || 0);
                        return (
                            <div key={order.orderId} className="receipt-card rounded-[10px] border border-slider bg-white p-[12px] shadow-[0_2px_8px_rgba(0,0,0,0.05)]">

                                {/* ── Admin Topbar: badge + date + order-id + info link ── */}
                                <div className="admin-card-topbar">
                                    <div className="admin-card-topbar-left">
                                        <span className={`order-type-badge ${isPhysical ? 'badge-physical' : 'badge-digital'}`}>
                                            {isPhysical ? 'Physical' : 'Digital'}
                                        </span>
                                        <span className="order-date">{formatDate(order.createdAt)}</span>
                                        <code className="order-id-code">#{order.orderId.slice(-8)}</code>
                                    </div>
                                    <Link to={`/admin/orders/${order.orderId}`} className="order-info-btn" title="View Full Details">
                                        <i className="fi fi-sr-info"></i>
                                    </Link>
                                </div>

                                {/* ── Receipt Meta: Status + Actions ── */}
                                <div className="receipt-meta">
                                    <div className={`receipt-meta-row${isYetToReceive(order) ? ' receipt-meta-row--warning' : ''}`}>
                                        <strong>Status:</strong>
                                        <div className="receipt-status-value">{renderStatus(order)}</div>
                                    </div>
                                    <button className="admin-truck-btn" onClick={() => handleEditClick(order)}>
                                        <i className="fi fi-rs-truck-side"></i> Update Tracking
                                    </button>
                                    <div className="admin-email-row">
                                        <div className="admin-email-info">
                                            <div className="admin-email-status">
                                                <i className="fi fi-rr-email-pending"></i>
                                                <span className={"email-pill email-pill--" + emailStatus}>
                                                    {emailStatus === 'sent'
                                                        ? (order.emailSentAt ? `Sent · ${formatDate(order.emailSentAt)}` : 'Sent ✅')
                                                        : emailStatus === 'failed' ? 'Failed ❌' : 'Pending ⏳'}
                                                </span>
                                            </div>
                                            {receiptTemplateName && (
                                                <span className="email-template-badge">{receiptTemplateName}</span>
                                            )}
                                        </div>
                                        <button className="admin-resend-btn" onClick={() => handleResendEmail(order.orderId)}>
                                            <i className="fi fi-rr-envelope"></i> Resend
                                        </button>
                                    </div>
                                </div>

                                <div className="receipt-divider"></div>

                                {/* ── Products ── */}
                                <div className="receipt-items">
                                    {order.products && order.products.map((p, idx) => (
                                        <div key={idx} className="receipt-item-row">
                                            <div className="item-name">
                                                {p.title || 'Product'} <span className="item-qty">x{p.quantity}</span>
                                            </div>
                                            {p.subtotal != null && (
                                                <div className="item-price">{formatPrice(p.subtotal, order.currency)}</div>
                                            )}
                                        </div>
                                    ))}
                                    {discount > 0 && (
                                        <div className="receipt-item-row" style={{ color: '#28a745' }}>
                                            <div className="item-name">Discount</div>
                                            <div className="item-price">-{formatPrice(discount, order.currency)}</div>
                                        </div>
                                    )}
                                </div>

                                <div className="receipt-divider"></div>

                                {/* ── Summary ── */}
                                <div className="receipt-summary">
                                    <div className="summary-row">
                                        <span>TOTAL</span>
                                        <span className="summary-total">{formatPrice(order.totalAmount || 0, order.currency)}</span>
                                    </div>
                                    <div className="summary-row-small">
                                        <span>Payment:</span>
                                        <span style={{ color: '#28a745' }}>
                                            {((order.payment && order.payment.status) || 'Pending').toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="summary-row-small">
                                        <span>Method:</span>
                                        <span>{((order.payment && order.payment.method) || 'N/A').toUpperCase()}</span>
                                    </div>
                                </div>

                            </div>
                        );
                    })}
                </div>
            )}

            {/* Edit Status Popup */}
            {editingOrder && (
                <div className="status-popup-overlay">
                    <div className="status-popup-wide"> {/* Changed to wide for better layout */}
                        <h3>Update Order Status</h3>
                        <p className="popup-order-id">ID: {editingOrder.orderId}</p>
                        
                        <form onSubmit={handleStatusUpdate}>
                            <div className="status-list-container">
                                {statusList.map((item, index) => (
                                    <div key={item.id} className="status-row-wrapper">
                                        <div className={`status-row ${item.checked ? 'active' : 'inactive'}`}>
                                            <div className="status-col-check">
                                                <input 
                                                    type="checkbox" 
                                                    checked={item.checked} 
                                                    onChange={(e) => updateStatusItem(item.id, 'checked', e.target.checked)}
                                                />
                                            </div>
                                            <div className="status-col-content">
                                                <div className="status-input-group">
                                                    {item.custom ? (
                                                        <input 
                                                            type="text" 
                                                            className="status-name-input"
                                                            value={item.status}
                                                            onChange={(e) => updateStatusItem(item.id, 'status', e.target.value)}
                                                            placeholder="Status Name"
                                                            required={item.checked}
                                                        />
                                                    ) : (
                                                        <span className="status-name-fixed">{item.status}</span>
                                                    )}
                                                </div>
                                                <div className="message-input-group">
                                                    <textarea 
                                                        className="status-msg-input"
                                                        value={item.message}
                                                        onChange={(e) => updateStatusItem(item.id, 'message', e.target.value)}
                                                        placeholder="Add a message (optional)"
                                                        rows="1"
                                                    />
                                                </div>
                                            </div>
                                            {item.custom && (
                                                <button type="button" className="remove-row-btn" onClick={() => removeStatusItem(item.id)} title="Remove">
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        
                                        {/* Add Button between items or at the end */}
                                        <div className="add-row-zone">
                                            <button type="button" className="add-between-btn" onClick={() => addStatusItem(index)} title="Add step here">
                                                +
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="popup-actions">
                                <button type="button" className="cancel-btn" onClick={() => setEditingOrder(null)}>Cancel</button>
                                <button type="submit" className="save-btn" disabled={updating}>
                                    {updating ? "Saving..." : "Save Updates"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
